import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { listings, Listing } from '../api/client';
import { config } from '../config';

const COUNTRY_MAP: Record<string, string> = {
  AE: 'United Arab Emirates', SA: 'Saudi Arabia', EG: 'Egypt', KW: 'Kuwait', BH: 'Bahrain', OM: 'Oman', QA: 'Qatar',
};

function getMapSearchQuery(listing: Listing): string {
  const loc = listing.location;
  if (!loc) return '';
  const parts = listing.address?.trim() ? [listing.address, loc.area, loc.city] : [loc.area, loc.city].filter(Boolean);
  const countryName = (loc.country && COUNTRY_MAP[loc.country]) || loc.country || '';
  return [...parts, countryName].filter(Boolean).join(', ') || '';
}

function geocodeUrl(query: string): string {
  const base = (config.apiBaseUrl || '').replace(/\/$/, '');
  if (base) return `${base}/public/geocode?q=${encodeURIComponent(query)}&limit=1`;
  return `https://nominatim.openstreetmap.org/search?${new URLSearchParams({ q: query, format: 'json', limit: '1' })}`;
}

export default function ListingsMap() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const [items, setItems] = useState<Listing[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [pageStack, setPageStack] = useState<Array<{ token: string; items: Listing[] }>>([]);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const type = searchParams.get('type') || '';
  const location = searchParams.get('location') || '';
  const country = searchParams.get('country') || '';
  const minPrice = searchParams.get('minPrice') || '';
  const maxPrice = searchParams.get('maxPrice') || '';
  const bedrooms = searchParams.get('bedrooms') || '';
  const category = searchParams.get('category') || '';
  const unitType = searchParams.get('unitType') || '';
  const amenities = searchParams.get('amenities') || '';
  const sort = searchParams.get('sort') || 'newest';
  const order = searchParams.get('order') || 'desc';

  const PAGE_SIZE = 12;
  const listParams: import('../api/client').ListingListParams = {
    type: type || undefined,
    limit: String(PAGE_SIZE),
    location: location.trim() || undefined,
    country: country.trim() || undefined,
    minPrice: minPrice.trim() || undefined,
    maxPrice: maxPrice.trim() || undefined,
    bedrooms: bedrooms.trim() || undefined,
    category: category.trim() || undefined,
    unitType: unitType.trim() || undefined,
    amenities: amenities.trim() || undefined,
    sort: sort === 'price' || sort === 'area' || sort === 'bedrooms' || sort === 'newest' ? sort : undefined,
    order: order === 'asc' || order === 'desc' ? order : undefined,
  };

  useEffect(() => {
    let cancelled = false;
    setPageStack([]);
    setNextToken(undefined);
    setLoading(true);
    setError(null);
    listings.list(listParams).then((res) => {
      if (!cancelled) {
        setItems(res.items ?? []);
        setNextToken(res.nextToken ?? undefined);
      }
    }).catch(() => {
      if (!cancelled) setError('Could not load listings');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [type, location, country, minPrice, maxPrice, bedrooms, category, unitType, amenities, sort, order]);

  const goToNextPage = () => {
    if (!nextToken || loading) return;
    setPageStack((prev) => [...prev, { token: nextToken, items: [...items] }]);
    setLoading(true);
    setError(null);
    listings.list({ ...listParams, nextToken }).then((res) => {
      setItems(res.items ?? []);
      setNextToken(res.nextToken ?? undefined);
    }).catch(() => setError('Could not load listings')).finally(() => setLoading(false));
  };

  const goToPreviousPage = () => {
    if (pageStack.length === 0 || loading) return;
    const stack = [...pageStack];
    const prev = stack.pop();
    if (!prev) return;
    setPageStack(stack);
    setItems(prev.items);
    setNextToken(prev.token);
  };

  useEffect(() => {
    if (!containerRef.current || items.length === 0) return;
    const map = L.map(containerRef.current).setView([26.8206, 30.8025], 6);
    mapRef.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);

    const markerIcon = L.divIcon({
      className: 'listings-map-marker',
      html: '<span style="background:#0d9488;width:20px;height:20px;border-radius:50%;display:block;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);"></span>',
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });

    let placed = 0;
    const maxGeocode = 25;
    const coords: { lat: number; lng: number; listing: Listing }[] = [];

    const run = (index: number) => {
      if (index >= items.length || placed >= maxGeocode) {
        if (coords.length > 0) {
          const bounds = L.latLngBounds(coords.map((c) => [c.lat, c.lng]));
          map.fitBounds(bounds, { padding: [20, 20], maxZoom: 14 });
        }
        setMapReady(true);
        return;
      }
      const listing = items[index];
      const query = getMapSearchQuery(listing);
      if (!query.trim()) {
        run(index + 1);
        return;
      }
      const url = geocodeUrl(query);
      fetch(url, {
        headers: url.includes('nominatim') ? { 'Accept': 'application/json', 'User-Agent': 'Darlynk/1.0' } : {},
      })
        .then((r) => r.json())
        .then((data: { lat?: string; lon?: string }[]) => {
          if (!mapRef.current || !data?.length) {
            run(index + 1);
            return;
          }
          const lat = parseFloat(data[0].lat ?? '');
          const lng = parseFloat(data[0].lon ?? '');
          if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
            coords.push({ lat, lng, listing });
            const title = (listing.titleEn || '').slice(0, 50);
            const marker = L.marker([lat, lng], { icon: markerIcon }).addTo(mapRef.current!);
            marker.bindPopup(
              `<div style="min-width:160px"><a href="/listings/${listing.listingId}" style="font-weight:600;color:#0d9488">${title}</a><br/><span style="font-size:12px;color:#666">${listing.price ?? ''} ${listing.currency ?? ''}</span></div>`
            );
            placed++;
          }
          setTimeout(() => run(index + 1), 220);
        })
        .catch(() => run(index + 1));
    };
    run(0);

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, [items]);

  const backUrl = `/listings${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

  return (
    <div style={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      <div className="listings-map-header" style={{ padding: '0.5rem 1rem', background: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <Link to={backUrl} style={{ color: '#0d9488', fontWeight: 600, textDecoration: 'none' }}>← {t('common.backToSearch')}</Link>
        <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>{items.length} {t('common.listingsCount', { count: items.length })}</span>
        <nav role="navigation" aria-label="Map listings pagination" className="home-pagination" style={{ margin: 0, marginLeft: 'auto', padding: '0.25rem 0', border: 'none', background: 'none' }}>
          <button type="button" onClick={goToPreviousPage} disabled={loading || pageStack.length === 0} className="home-pagination-btn home-pagination-btn--prev" style={{ padding: '0.4rem 0.75rem', fontSize: '0.85rem' }}>
            ← {t('common.previousPage')}
          </button>
          <span className="home-pagination-info">{t('common.pageN', { n: pageStack.length + 1 })}</span>
          <button type="button" onClick={goToNextPage} disabled={loading || !nextToken} className="home-pagination-btn home-pagination-btn--next" style={{ padding: '0.4rem 0.75rem', fontSize: '0.85rem' }}>
            {loading ? t('common.loading') : `${t('common.nextPage')} →`}
          </button>
        </nav>
      </div>
      {loading && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>{t('common.loading')}</div>
      )}
      {error && (
        <div style={{ padding: '1rem', background: '#fef2f2', color: '#b91c1c' }}>{error}</div>
      )}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          minHeight: 300,
          width: '100%',
          background: '#e5e7eb',
        }}
        aria-label="Map of listings"
      />
      {!loading && mapReady && items.length === 0 && (
        <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>{t('common.noListings')}</div>
      )}
    </div>
  );
}
